import React, { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Tag, Button, message } from "antd";
import {
  useSessionsByUserId,
  useUpdateSessionName,
} from "../../api/SessionsAPI";
import { useNavigate } from "react-router-dom";
import EditableName from "../common/EditableName";
import {
  agGridThemeVars,
  agGridDefaultColDef,
  agGridPinnedActionsCellStyle,
} from "../../constants/agGridTheme";
import { StatusTag, SessionTypeTag } from "../common/SessionTags";

const SessionHistoryTable = ({ userId, status, type }) => {
  const navigate = useNavigate();
  const {
    data: sessions,
    isLoading: sessionsLoading,
    error: sessionsError,
  } = useSessionsByUserId(userId, {
    status: status,
    includeCompletedCount: true,
    type: type,
  });
  const updateSessionNameMutation = useUpdateSessionName();

  // Sort sessions to prioritize in_progress
  const sortedSessions = useMemo(() => {
    if (!sessions) return [];
    return [...sessions].sort((a, b) => {
      // Put in_progress sessions first
      if (a.status === "in_progress" && b.status !== "in_progress") return -1;
      if (a.status !== "in_progress" && b.status === "in_progress") return 1;
      // For sessions with the same status, sort by created_at (newest first)
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [sessions]);

  const sessionTypeRenderer = (params) => (
    <SessionTypeTag isCohortSession={params.data.cohort_id !== null} />
  );

  const statusRenderer = (params) => <StatusTag status={params.value} />;

  const actionRenderer = (params) => {
    if (params.data.status === "in_progress") {
      return (
        <Button
          type="primary"
          size="small"
          onClick={() => navigate(`/session/${params.data.id}/empathize`)}
          className="text-xs"
        >
          Continue Session
        </Button>
      );
    }
    return null;
  };

  const columnDefs = [
    {
      headerName: "Name",
      field: "name",
      minWidth: 180,
      flex: 1,
      cellRenderer: (params) => (
        <EditableName
          value={params.data.name || params.data.display_name}
          fallback={
            params.data.cohort_id
              ? "Untitled Cohort Session"
              : "Untitled Session"
          }
          title="Edit session name"
          inputLabel="Session name"
          buttonVariant="icon"
          buttonAriaLabel="Edit session name"
          buttonClassName="!text-primary hover:!text-primaryLight"
          onSave={(name) =>
            new Promise((resolve, reject) => {
              updateSessionNameMutation.mutate(
                { sessionId: params.data.id, name },
                {
                  onSuccess: () => {
                    message.success("Session name updated");
                    resolve();
                  },
                  onError: (error) => {
                    message.error(
                      error.response?.data?.error ||
                        "Failed to update session name",
                    );
                    reject(error);
                  },
                },
              );
            })
          }
        />
      ),
    },
    {
      headerName: "Started At",
      field: "created_at",
      width: 160,
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    {
      headerName: "Status",
      field: "status",
      width: 130,
      cellRenderer: statusRenderer,
    },
    {
      headerName: "Session Type",
      field: "cohort_id",
      width: 140,
      cellRenderer: sessionTypeRenderer,
      sortable: true,
      filter: true,
    },
    {
      headerName: "Competencies Achieved",
      field: "completed_count",
      width: 150,
    },
    {
      headerName: "Actions",
      cellRenderer: actionRenderer,
      sortable: false,
      filter: false,
      pinned: "right",
      width: 150,
      cellStyle: agGridPinnedActionsCellStyle,
      headerComponentParams: {
        template: undefined,
      },
    },
  ];

  if (sessionsLoading) {
    return <div>Loading...</div>;
  }

  if (sessionsError) {
    return <div>Error loading sessions: {sessionsError.message}</div>;
  }

  return (
    <div
      className="ag-theme-alpine"
      style={{
        height: 400,
        width: "100%",
        ...agGridThemeVars,
      }}
    >
      <AgGridReact
        rowData={sortedSessions}
        columnDefs={columnDefs}
        defaultColDef={agGridDefaultColDef}
        pagination={true}
        paginationPageSize={10}
      />
    </div>
  );
};

export default SessionHistoryTable;
